/**
 * App-Rahmen: Seiten (Router), Tab-Leiste, erster Start, Update-Hinweis.
 * Neue Seite hinzufügen = Datei in pages/ anlegen und unten eine <Route> eintragen.
 */
import { useEffect, useState } from 'react';
import { BrowserRouter, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router';
import { CloudSun, Leaf, Settings as SettingsIcon, Sun } from 'lucide-react';
import { useStore, refresh } from './lib/store';
import { getToken } from './lib/api';
import { syncSubscription } from './lib/push';
import { TodayPage } from './pages/Today';
import { PlantsPage } from './pages/Plants';
import { PlantPage } from './pages/Plant';
import { PlantEditPage } from './pages/PlantEdit';
import { WeatherPage } from './pages/Weather';
import { SettingsPage } from './pages/Settings';
import { BackupPage, CalendarPage, DiagnosticsPage, ShortcutsPage } from './pages/SettingsSub';
import { Onboarding } from './pages/Onboarding';
import { SitterPage } from './pages/Sitter';
import { cx } from './ui/kit';

const TABS = [
  { to: '/', label: 'Heute', icon: Sun, end: true },
  { to: '/pflanzen', label: 'Pflanzen', icon: Leaf, end: false },
  { to: '/wetter', label: 'Wetter', icon: CloudSun, end: false },
  { to: '/einstellungen', label: 'Einstellungen', icon: SettingsIcon, end: false },
];

function TabBar() {
  return (
    <nav className="pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-line bg-paper/90 backdrop-blur-xl">
      <div className="mx-auto grid max-w-lg grid-cols-4">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) => cx('flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold', isActive ? 'text-moss' : 'text-muted')}
          >
            <t.icon className="h-6 w-6" strokeWidth={1.8} />
            {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** Service Worker registrieren; bei neuer Version einen Hinweis zeigen. */
function useServiceWorker(): { update: (() => void) | null } {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  useEffect(() => {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
    void navigator.serviceWorker.register('/sw.js', { type: 'module', scope: '/' }).then((reg) => {
      if (reg.waiting) setWaiting(reg.waiting);
      reg.addEventListener('updatefound', () => {
        const nw = reg.installing;
        nw?.addEventListener('statechange', () => {
          if (nw.state === 'installed' && navigator.serviceWorker.controller) setWaiting(nw);
        });
      });
      // Beim Zurückkehren in die App nach Updates schauen
      document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void reg.update());
    });
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!reloaded) {
        reloaded = true;
        location.reload();
      }
    });
  }, []);
  return { update: waiting ? () => waiting.postMessage({ type: 'SKIP_WAITING' }) : null };
}

/** Nachrichten vom Service Worker (Tipp auf Mitteilung bei älterem iOS) + Abgleich beim Zurückkehren. */
function useAppEvents() {
  const nav = useNavigate();
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e.data?.type === 'NAVIGATE' && typeof e.data.url === 'string') {
        const u = new URL(e.data.url, location.origin);
        nav(u.pathname + u.search);
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Bei jedem Start: Push-Abo beim Server erneuern (repariert sich so selbst)
    void syncSubscription().catch(() => false);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', onMsg);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [nav]);
}

function Shell() {
  const { state, ready, online, pending } = useStore();
  const [hasToken, setHasToken] = useState<boolean | null>(null);
  // Einrichtung läuft, bis sie selbst „fertig“ meldet (sonst verschwände der Code zu früh)
  const [setupDone, setSetupDone] = useState(() => localStorage.getItem('pflanzen-setup') === '1');
  const { update } = useServiceWorker();
  const loc = useLocation();
  useAppEvents();
  useEffect(() => {
    void getToken().then((t) => setHasToken(!!t));
  }, [state]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  const finishSetup = () => {
    localStorage.setItem('pflanzen-setup', '1');
    setSetupDone(true);
  };

  if (loc.pathname.startsWith('/sitter')) return <SitterPage />;
  if (!ready || hasToken === null) return <div className="paper min-h-dvh" />;
  if (!hasToken || !setupDone) return <Onboarding onDone={finishSetup} />;
  if (!state) return <div className="paper pt-safe min-h-dvh px-5 pt-16 text-muted">Lade deine Pflanzen …</div>;


  return (
    <div className="paper min-h-dvh pb-24">
      {(!online || pending.length > 0) && (
        <div className="pt-safe sticky top-0 z-30 bg-sun-soft px-4 pb-1.5 text-center text-xs font-semibold text-soil">
          {online ? `${pending.length} Änderung(en) werden übertragen …` : 'Offline – Abhaken geht trotzdem, wird später übertragen.'}
        </div>
      )}
      {update && (
        <button type="button" onClick={update} className="fixed inset-x-4 bottom-24 z-50 rounded-2xl bg-ink p-3.5 text-left text-sm font-semibold text-paper shadow-lg">
          ✨ Neue Version verfügbar – tippen zum Aktualisieren
        </button>
      )}
      <main className="mx-auto max-w-lg">
        <Routes>
          <Route path="/" element={<TodayPage />} />
          <Route path="/pflanzen" element={<PlantsPage />} />
          <Route path="/pflanzen/neu" element={<PlantEditPage />} />
          <Route path="/pflanzen/:id" element={<PlantPage />} />
          <Route path="/pflanzen/:id/bearbeiten" element={<PlantEditPage />} />
          <Route path="/wetter" element={<WeatherPage />} />
          <Route path="/einstellungen" element={<SettingsPage />} />
          <Route path="/einstellungen/kurzbefehle" element={<ShortcutsPage />} />
          <Route path="/einstellungen/kalender" element={<CalendarPage />} />
          <Route path="/einstellungen/sicherung" element={<BackupPage />} />
          <Route path="/einstellungen/diagnose" element={<DiagnosticsPage />} />
          <Route path="*" element={<TodayPage />} />
        </Routes>
      </main>
      <TabBar />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
